import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { BoxV2 as Box, Flex, Wizard, WizardStep } from 'portal-commons';
import Modal from '@/components/ModalV2';
import StepUsecase from './StepUsecase';
import StepRouting from './StepRouting';
import StepDetails from './StepDetails';
import { ProgramRegistrationFormData } from './types';
import { DefaultProgramRegistrationFormData } from './constant';
import {
  campaignBuilderController,
  UseCase,
  CampaignBuilderResult,
} from '@/apis/core';
import { SelectItem } from '@/utils/types';
import { toastFlashMessage } from '@/utils';

const CHANNEL_SMS = 'SMS';

interface RegistrationModalProps {
  onCancel: () => void;
  onSucceed: (data: CampaignBuilderResult) => void;
  useCases: UseCase[];
  cnpOptions: SelectItem[];
}

const Steps: WizardStep[] = [
  { key: 'usecase', label: 'Use Case Selection' },
  { key: 'routing', label: 'Routing & Connectivity' },
  { key: 'details', label: 'Program Details' },
];

const preparePayload = (data: ProgramRegistrationFormData) => {
  const samples: Record<string, string> = {};
  data.details.sampleMessages.forEach((item, idx) => {
    if (item.message) {
      samples[`sample${idx + 1}`] = item.message;
    }
  });

  const extraAttributes = {
    offNetOutboundTerminationSupported:
      data.routing.offNetOutboundTerminationSupported,
    intlInboundTerminationSupported:
      data.routing.intlInboundTerminationSupported,
    intlOutboundTerminationSupported:
      data.routing.intlOutboundTerminationSupported,
    onNetInboundTerminationSupported:
      data.routing.onNetInboundTerminationSupported,
    offNetInboundTerminationSupported:
      data.routing.offNetInboundTerminationSupported,
    expectedMessageVolume: Number(data.details.expectedMessageVolume),
  };

  return {
    description: data.details.description,
    referenceId: data.details.referenceId || null,
    supportingDocumentUuid: data.details.supportingDocumentUuid,
    ...samples,
    embeddedLink: data.details.embeddedLink,
    embeddedLinkSample: data.details.embeddedLink
      ? data.details.embeddedLinkSample
      : '',
    embeddedPhone: data.details.embeddedPhone,
    routingMode: data.routing.routingMode,
    mnoIds: data.routing.mnoIds,
    ...extraAttributes,
    // Populate default data for fields not exposed in the UI
    countryCode: 'HU',
    channelId: CHANNEL_SMS,
    subscriberOptin: true,
    subscriberOptout: true,
    subscriberHelp: true,
    termsAndConditions: true,
    directLending: false,
    numberPool: false,
    ageGated: false,
    optinKeywords: null,
    optoutKeywords: null,
    helpKeywords: null,
    termsAndConditionsLink: '',
    privacyPolicyLink: '',
    optinMessage: '',
    messageFlow:
      'Information was not provided during submission from the Portal',
    optoutMessage:
      'Information was not provided during submission from the Portal',
    helpMessage:
      'Information was not provided during submission from the Portal',
    resellerUid: 'R000000',
  };
};

const RegistrationModal: React.FC<RegistrationModalProps> = ({
  onCancel,
  onSucceed,
  useCases,
  cnpOptions,
}) => {
  const [step, setStep] = useState(0);
  const activeStepKey = Steps[step].key as keyof ProgramRegistrationFormData;

  const methods = useForm<ProgramRegistrationFormData>({
    mode: 'onTouched',
    defaultValues: DefaultProgramRegistrationFormData,
  });

  const handleNext = async () => {
    const isStepValid = await methods.trigger(activeStepKey);
    if (isStepValid) {
      setStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const onSubmit = async (data: ProgramRegistrationFormData) => {
    try {
      const { brandUid, usecase: usecaseId } = data.usecase;
      const cnpUid = data.routing.upstreamCnpUid;
      const payload = preparePayload(data);

      const result = await campaignBuilderController.createCampaign(
        brandUid,
        usecaseId,
        cnpUid,
        payload
      );

      if (result) {
        onSucceed(result);
      }
    } catch (err) {
      toastFlashMessage('Failed to create messaging program.', 'error');
    }
  };

  return (
    <Modal
      open
      onClose={onCancel}
      data-testid="registrationModal"
      sx={{
        width: '860px',
        maxHeight: 'calc(100% - 196px)',
        padding: '24px',
        borderRadius: '8px',
        overflow: 'auto',
      }}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={(e) => {
            if (step === Steps.length - 1) {
              methods.handleSubmit(onSubmit)(e);
            } else {
              e.preventDefault();
              handleNext();
            }
          }}
          style={{ width: '100%' }}
        >
          <Flex sx={{ flexDirection: 'column', gap: 'xl', width: '100%' }}>
            <Box sx={{ pb: '20px' }}>
              <Wizard steps={Steps} activeKey={activeStepKey} />
            </Box>

            {activeStepKey === 'usecase' && (
              <StepUsecase
                onNext={handleNext}
                onCancel={onCancel}
                useCases={useCases}
              />
            )}

            {activeStepKey === 'routing' && (
              <StepRouting
                onNext={handleNext}
                onPrevious={handleBack}
                cnpOptions={cnpOptions}
              />
            )}

            {activeStepKey === 'details' && (
              <StepDetails onPrevious={handleBack} />
            )}
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default RegistrationModal;
